import { useCallback, useEffect, useRef, useState } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  useParams,
  useNavigate,
  useLocation,
} from "react-router-dom";
import {
  ClerkFailed,
  ClerkLoading,
  ClerkProvider,
  SignedIn,
  SignedOut,
  SignIn,
  useAuth,
  useClerk,
} from "@clerk/clerk-react";
import { useTripStore } from "./store/tripStore";
import { useTripsListStore } from "./store/tripsListStore";
import { useLibraryStore } from "./store/libraryStore";
import { useUserStore } from "./store/userStore";
import { syncAuthState, useAuthStore } from "./store/authStore";
import { useInstanceSettingsStore } from "./store/instanceSettingsStore";
import { TripBoardShell } from "./components/TripBoardShell";
import { TripsPage } from "./pages/TripsPage";
import { SettingsPage } from "./pages/SettingsPage";
import { LibraryPage } from "./pages/LibraryPage";
import { TagsPage } from "./pages/TagsPage";
import { InvitePage } from "./pages/InvitePage";
import { CollectionsPage } from "./pages/CollectionsPage";
import { CollectionInvitePage } from "./pages/CollectionInvitePage";
import { useCollectionsStore } from "./store/collectionsStore";
import { useTripSidebarFilterStore } from "./store/tripSidebarFilterStore";
import { useUIStore } from "./store/uiStore";
import { UserIdentityModal } from "./components/UserIdentityModal";
import { setClerkInstance } from "./services/api";
import { setClerkInstanceForTokens } from "./services/apiTokenService";
import {
  enterStartedOfflineMode,
  startConnectivityWatcher,
  useConnectivityStore,
} from "./store/connectivityStore";
import {
  getLibrary as getSavedLibrary,
  getMeta as getOfflineMeta,
  getTripsList as getSavedTripsList,
  OFFLINE_SCHEMA_VERSION,
  type OfflineMeta,
} from "./services/offlineStore";
import {
  prepareOfflineCopy,
  startOfflineSync,
  syncAll,
} from "./services/offlineSync";
import { OfflineUnavailablePage } from "./pages/OfflineUnavailablePage";
import { AppPrompts } from "./components/AppPrompts";
import { Button } from "./components/Button";

// Clerk publishable key from environment
const CLERK_PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

if (!CLERK_PUBLISHABLE_KEY) {
  throw new Error("Missing VITE_CLERK_PUBLISHABLE_KEY environment variable");
}

// A clerk-js script that cannot load may hang rather than fail; after this
// long, open the saved copy if there is one.
const CLERK_LOAD_TIMEOUT_MS = 8_000;

// Loading screen component
function LoadingScreen({ message = "Loading..." }: { message?: string }) {
  return (
    <div className="flex items-center justify-center min-h-screen bg-page">
      <div className="flex flex-col items-center gap-4">
        <svg
          width="32"
          height="32"
          viewBox="0 0 48 48"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="animate-pulse"
        >
          <path
            d="M44 11.2727C44 14.0109 39.8386 16.3957 33.69 17.6364C39.8386 18.877 44 21.2618 44 24C44 26.7382 39.8386 29.123 33.69 30.3636C39.8386 31.6043 44 33.9891 44 36.7273C44 40.7439 35.0457 44 24 44C12.9543 44 4 40.7439 4 36.7273C4 33.9891 8.16144 31.6043 14.31 30.3636C8.16144 29.123 4 26.7382 4 24C4 21.2618 8.16144 18.877 14.31 17.6364C8.16144 16.3957 4 14.0109 4 11.2727C4 7.25611 12.9543 4 24 4C35.0457 4 44 7.25611 44 11.2727Z"
            fill="currentColor"
          />
        </svg>
        <span className="text-sm text-secondary font-medium">{message}</span>
      </div>
    </div>
  );
}

// Error screen component
function ErrorScreen({
  error,
  onRetry,
}: {
  error: string;
  onRetry: () => void;
}) {
  return (
    <div className="flex items-center justify-center min-h-screen bg-page">
      <div className="bg-white rounded-2xl p-10 max-w-[400px] text-center border border-border">
        <span className="material-symbols-outlined text-4xl text-error mb-4 block">
          error
        </span>
        <h2 className="text-lg font-bold mb-2">Something went wrong</h2>
        <p className="text-sm text-secondary mb-6">{error}</p>
        <button
          onClick={onRetry}
          className="px-6 py-2.5 bg-primary text-white border-none rounded-lg text-sm font-bold cursor-pointer font-sans"
        >
          Retry
        </button>
      </div>
    </div>
  );
}

// Sync Clerk auth state to our store and API
function ClerkAuthSync() {
  const { isLoaded, isSignedIn, userId } = useAuth();
  const clerk = useClerk();

  useEffect(() => {
    const email = clerk.user?.primaryEmailAddress?.emailAddress;
    syncAuthState(isLoaded, isSignedIn ?? false, userId ?? null, email);
  }, [isLoaded, isSignedIn, userId, clerk.user]);

  // A different user signing in on this device must not see the last
  // user's saved trips: wipe them before anything is synced.
  useEffect(() => {
    if (isLoaded && isSignedIn && userId) void prepareOfflineCopy(userId);
  }, [isLoaded, isSignedIn, userId]);

  useEffect(() => {
    if (isLoaded && clerk) {
      setClerkInstance(clerk);
      setClerkInstanceForTokens(clerk);
    }
  }, [isLoaded, clerk]);

  return null;
}

// Trip board route - loads specific trip
function TripBoardRoute() {
  const { tripId } = useParams<{ tripId: string }>();
  const navigate = useNavigate();
  const { trip, loading, error, loadTrip, unloadTrip } = useTripStore();
  const { setActiveTripId } = useTripsListStore();
  const resetSidebarFilters = useTripSidebarFilterStore((s) => s.resetFilters);
  const resetPlannerPosition = useUIStore((s) => s.resetPlannerPosition);

  useEffect(() => {
    if (tripId) {
      setActiveTripId(tripId);
      loadTrip(tripId).then((found) => {
        if (!found) {
          navigate("/");
        }
      });
    }

    return () => {
      unloadTrip();
      setActiveTripId(null);
      // Sidebar filters are shared across the trip's views; drop them when
      // leaving the trip so they do not carry into the next one.
      resetSidebarFilters();
      // The next trip opens on today (while under way) or its first day.
      resetPlannerPosition();
    };
  }, [
    tripId,
    loadTrip,
    unloadTrip,
    setActiveTripId,
    navigate,
    resetSidebarFilters,
    resetPlannerPosition,
  ]);

  if (loading || !trip) {
    return <LoadingScreen message="Loading trip..." />;
  }

  if (error) {
    return (
      <ErrorScreen error={error} onRetry={() => tripId && loadTrip(tripId)} />
    );
  }

  return <TripBoardShell />;
}

// App wrapper that handles initial loading
function AppContent({ onOffline }: { onOffline: () => void }) {
  const location = useLocation();
  const offline = useConnectivityStore((s) => s.mode === "offline");
  const { isLoaded, isSignedIn } = useAuth();
  const {
    loading: tripsLoading,
    error: tripsError,
    loadTrips,
  } = useTripsListStore();
  const {
    loading: libraryLoading,
    error: libraryError,
    loadLibrary,
  } = useLibraryStore();
  const { loading: settingsLoading, loadSettings } = useInstanceSettingsStore();
  const { loadCollections } = useCollectionsStore();
  const { displayName, setDisplayName } = useUserStore();
  const [initialized, setInitialized] = useState(false);

  // Check if we're on special routes (need handling before auth check)
  const isInviteRoute =
    location.pathname.startsWith("/invite/") &&
    !location.pathname.startsWith("/invite/collection/");
  const isCollectionInviteRoute = location.pathname.startsWith(
    "/invite/collection/",
  );

  // Load data once authenticated
  useEffect(() => {
    if (isLoaded && isSignedIn && !initialized) {
      const init = async () => {
        await Promise.all([
          loadLibrary(),
          loadTrips(),
          loadSettings(),
          loadCollections(),
        ]);
        setInitialized(true);
        // Save the list, library and every changed trip to this device.
        if (!useTripsListStore.getState().error) void syncAll();
      };
      init();
    }
  }, [
    isLoaded,
    isSignedIn,
    loadLibrary,
    loadTrips,
    loadSettings,
    loadCollections,
    initialized,
  ]);

  // Invite routes - render with special handling
  if (isInviteRoute) {
    return (
      <Routes>
        <Route path="/invite/:token" element={<InvitePage />} />
      </Routes>
    );
  }

  if (isCollectionInviteRoute) {
    return (
      <Routes>
        <Route
          path="/invite/collection/:token"
          element={<CollectionInvitePage />}
        />
      </Routes>
    );
  }

  // Wait for Clerk to load
  if (!isLoaded) {
    return <LoadingScreen message="Checking authentication..." />;
  }

  // Load data
  const loading =
    !initialized || tripsLoading || libraryLoading || settingsLoading;
  const error = tripsError || libraryError;

  if (error && offline) {
    return <OfflineHandoff onOffline={onOffline} />;
  }

  if (loading) {
    return <LoadingScreen message="Loading PlotPlanGo..." />;
  }

  if (error) {
    return (
      <ErrorScreen
        error={error}
        onRetry={() => {
          setInitialized(false);
          Promise.all([
            loadLibrary(),
            loadTrips(),
            loadSettings(),
            loadCollections(),
          ]).then(() => setInitialized(true));
        }}
      />
    );
  }

  return (
    <>
      {!displayName && <UserIdentityModal onSubmit={setDisplayName} />}
      <Routes>
        <Route path="/" element={<TripsPage />} />
        <Route path="/trips/:tripId" element={<TripBoardRoute />} />
        <Route
          path="/library"
          element={offline ? <OfflineUnavailablePage /> : <LibraryPage />}
        />
        <Route
          path="/collections"
          element={offline ? <OfflineUnavailablePage /> : <CollectionsPage />}
        />
        <Route
          path="/tags"
          element={offline ? <OfflineUnavailablePage /> : <TagsPage />}
        />
        <Route
          path="/settings"
          element={offline ? <OfflineUnavailablePage /> : <SettingsPage />}
        />
      </Routes>
    </>
  );
}

/** Start-up data could not load because the network is down. */
function OfflineHandoff({ onOffline }: { onOffline: () => void }) {
  useEffect(() => {
    onOffline();
  }, [onOffline]);
  return <LoadingScreen message="Opening saved trips..." />;
}

function ClerkFailedTrigger({ onFailed }: { onFailed: () => void }) {
  useEffect(() => {
    onFailed();
  }, [onFailed]);
  return null;
}

/**
 * The app started without a connection: the same planner routes, filled
 * from the copy saved on this device and read-only throughout.
 */
function OfflineApp({ meta }: { meta: OfflineMeta }) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void Promise.all([getSavedTripsList(), getSavedLibrary()]).then(
      ([trips, library]) => {
        if (cancelled) return;
        enterStartedOfflineMode(meta.lastSyncedAt);
        // Trust the last signed-in user on this device (ownership checks
        // such as isOwnPoi read it).
        useAuthStore.setState({
          status: "authenticated",
          userId: meta.userId,
          user: { id: meta.userId },
        });
        useTripsListStore.setState({
          trips: trips ?? [],
          loading: false,
          error: null,
        });
        useLibraryStore.setState({
          pois: library?.pois ?? [],
          tags: library?.tags ?? [],
          categoryTags: library?.categoryTags ?? [],
          loading: false,
          error: null,
        });
        setReady(true);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [meta]);

  if (!ready) return <LoadingScreen message="Opening saved trips..." />;

  return (
    <Routes>
      <Route path="/" element={<TripsPage />} />
      <Route path="/trips/:tripId" element={<TripBoardRoute />} />
      <Route path="*" element={<OfflineUnavailablePage />} />
    </Routes>
  );
}

function OfflineSignInScreen() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-page px-4">
      <div className="flex max-w-sm flex-col items-center gap-3 text-center">
        <span
          className="material-symbols-outlined text-4xl text-tertiary"
          aria-hidden="true"
        >
          cloud_off
        </span>
        <h1 className="text-lg font-bold text-primary">You're offline</h1>
        <p className="text-sm text-secondary">
          Connect to sign in. Trips you open while signed in are saved on this
          device so you can view them offline next time.
        </p>
        <Button
          variant="primary"
          icon="refresh"
          onClick={() => window.location.reload()}
        >
          Try again
        </Button>
      </div>
    </div>
  );
}

type OfflineBoot = { kind: "saved"; meta: OfflineMeta } | { kind: "none" };

/**
 * Chooses between the live app and the saved copy. Starting offline, Clerk
 * failing, or Clerk not loading in time opens the saved copy when this
 * device has one for the last signed-in user.
 */
function AppGate() {
  const { isLoaded, isSignedIn, userId } = useAuth();
  const [offlineBoot, setOfflineBoot] = useState<OfflineBoot | null>(null);
  const checkingSavedCopy = useRef(false);

  const bootOffline = useCallback(
    (options: { expectedUserId?: string | null; certain: boolean }) => {
      if (checkingSavedCopy.current) return;
      checkingSavedCopy.current = true;
      void getOfflineMeta().then((meta) => {
        const usable =
          meta &&
          meta.schemaVersion === OFFLINE_SCHEMA_VERSION &&
          (!options.expectedUserId || meta.userId === options.expectedUserId);
        if (usable) {
          setOfflineBoot({ kind: "saved", meta });
        } else if (options.certain) {
          setOfflineBoot({ kind: "none" });
        } else {
          // Maybe just a slow connection: keep waiting for Clerk.
          checkingSavedCopy.current = false;
        }
      });
    },
    [],
  );

  useEffect(() => {
    if (isLoaded) return;
    if (!navigator.onLine) {
      bootOffline({ certain: true });
      return;
    }
    const timer = window.setTimeout(
      () => bootOffline({ certain: false }),
      CLERK_LOAD_TIMEOUT_MS,
    );
    return () => window.clearTimeout(timer);
  }, [isLoaded, bootOffline]);

  // clerk-js served from the HTTP cache cannot reach its API and reports
  // signed out; with no network that is not a real sign-out.
  useEffect(() => {
    if (isLoaded && !isSignedIn && !navigator.onLine) {
      bootOffline({ certain: true });
    }
  }, [isLoaded, isSignedIn, bootOffline]);

  const onClerkFailed = useCallback(
    () => bootOffline({ certain: true }),
    [bootOffline],
  );
  const onDataOffline = useCallback(
    () => bootOffline({ expectedUserId: userId, certain: true }),
    [bootOffline, userId],
  );

  if (offlineBoot?.kind === "saved") {
    return <OfflineApp meta={offlineBoot.meta} />;
  }
  if (offlineBoot?.kind === "none") {
    return <OfflineSignInScreen />;
  }

  return (
    <>
      <ClerkAuthSync />
      <ClerkLoading>
        <LoadingScreen message="Checking authentication..." />
      </ClerkLoading>
      <ClerkFailed>
        <ClerkFailedTrigger onFailed={onClerkFailed} />
      </ClerkFailed>
      <SignedOut>
        <LoginPage />
      </SignedOut>
      <SignedIn>
        <AppContent onOffline={onDataOffline} />
      </SignedIn>
    </>
  );
}

// Login page using Clerk's SignIn component
function LoginPage() {
  return (
    <div className="flex items-center justify-center min-h-screen bg-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold mb-2">Welcome to PlotPlanGo</h1>
          <p className="text-secondary">Sign in to start planning your trips</p>
        </div>
        <SignIn
          appearance={{
            elements: {
              rootBox: "mx-auto",
              card: "shadow-lg",
            },
          }}
        />
      </div>
    </div>
  );
}

export function App() {
  useEffect(() => {
    startConnectivityWatcher();
    startOfflineSync();
  }, []);

  return (
    <ClerkProvider publishableKey={CLERK_PUBLISHABLE_KEY}>
      <BrowserRouter>
        <AppGate />
      </BrowserRouter>
      <AppPrompts />
    </ClerkProvider>
  );
}
